import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import {
  RegionTypeSchema,
  WorkStatusTypeSchema,
  yourDetailsOnboardingFormSchema,
  mapYourDetailsOnboardingFormForToFrontend,
  YesNoTypeSchema,
  AvailabilityTypeSchema,
  mapYourDetailsOnboardingFormForBackend
} from "./types";
import { Card, CardContent } from "../ui/card";
import { Button, Form, FormField, FormInput, FormLabel } from "../ui";
import { ActivityIndicator, View } from "react-native";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { Recruitment } from "../types/Recruitment";
import { trpc } from "@/trpc/react";
import { Link, router } from "expo-router";
import Toast from "react-native-toast-message";
import { Ionicons } from "@expo/vector-icons";
import { Text } from "../ui/text";

interface YourDetailsOnboardingFormProps {
  recruitmentData: Recruitment
  onFormSuccess: () => void
}

export default function YourDetailsOnboardingForm({ recruitmentData, onFormSuccess }: YourDetailsOnboardingFormProps) {

  const insets = useSafeAreaInsets();
  const contentInsets = {
    top: insets.top,
    bottom: insets.bottom,
    left: 12,
    right: 12,
  }

  const form = useForm<z.infer<typeof yourDetailsOnboardingFormSchema>>({
    resolver: zodResolver(yourDetailsOnboardingFormSchema),
    defaultValues: mapYourDetailsOnboardingFormForToFrontend(recruitmentData)
  });


  const { mutate: updateRecruitment } = trpc.recruitment.update.useMutation({
    onSuccess(_response) {
      onFormSuccess();
      router.back();
      Toast.show({
        type: 'success',
        text1: 'Successfully saved your details'
      });
    },
  });

  async function onSubmit(data: z.infer<typeof yourDetailsOnboardingFormSchema>) {
    const mappedDetails = mapYourDetailsOnboardingFormForBackend(recruitmentData.id, data);
    updateRecruitment(mappedDetails);
  }

  const styles = {
    labelContainer: {
      flexDirection: 'row' as const,
      alignItems: 'center' as const,
      marginBottom: 4,
    },
    inputLabel: {
      marginLeft: 6,
      fontSize: 13,
      fontWeight: '500' as const,
      color: '#444',
    }
  };

  return (
    <Card className='mt-4 mb-20'>
      <CardContent>
        <Form {...form}>
          <View className="gap-4 mb-4 mt-8">
            <FormField
              control={form.control}
              name="first_name"
              render={({ field }) => (
                <FormInput
                  label="First name"
                  {...field}
                />
              )}
            />
            <FormField
              control={form.control}
              name="last_name"
              render={({ field }) => (
                <FormInput
                  label="Surname"
                  {...field}
                />
              )}
            />
            <View>
              <FormLabel>Region</FormLabel>
              <FormField
                control={form.control}
                name="region"
                render={({ field }) => (
                  <Select onValueChange={(option) => field.onChange(option?.value || '')} value={{ value: field.value || "", label: field.value || "" }}>
                    <SelectTrigger className='w-[250px]'>
                      <SelectValue
                        className='text-foreground text-sm native:text-lg'
                        placeholder='Select a region'
                      />
                    </SelectTrigger>
                    <SelectContent insets={contentInsets} className='w-[250px] z-[1000] bg-white shadow-lg'>
                      <SelectGroup>
                        {RegionTypeSchema.options.map((type, index) => (
                          <SelectItem key={index} label={type} value={type}>
                            {type}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                )}
              />
            </View>

            <FormField
              control={form.control}
              name="address_line_1"
              render={({ field }) => (
                <>
                  <View style={styles.labelContainer}>
                    <Ionicons name="location" size={16} color="#666" />
                    <FormLabel style={styles.inputLabel}>Address Line 1</FormLabel>
                  </View>
                  <FormInput
                    label=""
                    {...field}
                  />
                </>
              )}
            />
            <FormField
              control={form.control}
              name="address_line_2"
              render={({ field }) => (
                <>
                  <View style={styles.labelContainer}>
                    <Ionicons name="location" size={16} color="#666" />
                    <FormLabel style={styles.inputLabel}>Address Line 2</FormLabel>
                  </View>
                  <FormInput
                    label=""
                    {...field}
                  />
                </>
              )}
            />
            <FormField
              control={form.control}
              name="address_city"
              render={({ field }) => (
                <>
                  <View style={styles.labelContainer}>
                    <Ionicons name="business" size={16} color="#666" />
                    <FormLabel style={styles.inputLabel}>City</FormLabel>
                  </View>
                  <FormInput
                    label=""
                    {...field}
                  />
                </>
              )}
            />
            <FormField
              control={form.control}
              name="address_county"
              render={({ field }) => (
                <>
                  <View style={styles.labelContainer}>
                    <Ionicons name="map" size={16} color="#666" />
                    <FormLabel style={styles.inputLabel}>County</FormLabel>
                  </View>
                  <FormInput
                    label=""
                    {...field}
                  />
                </>
              )}
            />
            <FormField
              control={form.control}
              name="address_postcode"
              render={({ field }) => (
                <>
                  <View style={styles.labelContainer}>
                    <Ionicons name="navigate" size={16} color="#666" />
                    <FormLabel style={styles.inputLabel}>Postcode</FormLabel>
                  </View>
                  <FormInput
                    label=""
                    {...field}
                  />
                </>
              )}
            />
            <FormField
              control={form.control}
              name="address_country"
              render={({ field }) => (
                <>
                  <View style={styles.labelContainer}>
                    <Ionicons name="globe" size={16} color="#666" />
                    <FormLabel style={styles.inputLabel}>Country</FormLabel>
                  </View>
                  <FormInput
                    label=""
                    {...field}
                  />
                </>
              )}
            />
            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormInput
                  label="Contact number"
                  keyboardType="phone-pad"
                  {...field}
                />
              )}
            />
            <View>
              <FormLabel>Current work status</FormLabel>
              <FormField
                control={form.control}
                name="work_status"
                render={({ field }) => (

                  <Select onValueChange={(option) => field.onChange(option?.value || '')} value={{ value: field.value || "", label: field.value || "" }}>
                    <SelectTrigger className='w-[250px]'>
                      <SelectValue
                        className='text-foreground text-sm native:text-lg'
                        placeholder='Select a work status'
                      />
                    </SelectTrigger>
                    <SelectContent insets={contentInsets} className='w-[250px] z-[1000] bg-white shadow-lg'>
                      <SelectGroup>
                        {WorkStatusTypeSchema.options.map((type, index) => (
                          <SelectItem key={index} label={type} value={type}>
                            {type}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                )}
              />
            </View>
            <View>
              <FormLabel>Do you have the right to work in the UK?</FormLabel>
              <FormField
                control={form.control}
                name="work_permit"
                render={({ field }) => (

                  <Select onValueChange={(option) => field.onChange(option?.value || '')} value={{ value: field.value || "", label: field.value || "" }}>
                    <SelectTrigger className='w-[250px]'>
                      <SelectValue
                        className='text-foreground text-sm native:text-lg'
                        placeholder='Select a work permit status'
                      />
                    </SelectTrigger>
                    <SelectContent insets={contentInsets} className='w-[250px] z-[1000] bg-white shadow-lg'>
                      <SelectGroup>
                        {YesNoTypeSchema.options.map((type, index) => (
                          <SelectItem key={index} label={type} value={type}>
                            {type}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                )}
              />
            </View>
            <View>
              <FormLabel>If you are not a UK resident, please enter your <Link href="https://www.gov.uk/view-prove-immigration-status" className="text-primary hover:underline">GOV.UK immigration share code</Link></FormLabel>
              <FormField
                control={form.control}
                name="immigration_share_code"
                render={({ field }) => (
                  <FormInput
                    {...field}
                  />
                )}
              />
            </View>
            <View>
              <FormLabel>What is your current availability?</FormLabel>
              <FormField
                control={form.control}
                name="current_availability"
                render={({ field }) => (

                  <Select onValueChange={(option) => field.onChange(option?.value || '')} value={{ value: field.value || "", label: field.value || "" }}>
                    <SelectTrigger className='w-[250px]'>
                      <SelectValue
                        className='text-foreground text-sm native:text-lg'
                        placeholder='Select availability'
                      />
                    </SelectTrigger>
                    <SelectContent insets={contentInsets} className='w-[250px] z-[1000] bg-white shadow-lg'>
                      <SelectGroup>
                        {AvailabilityTypeSchema.options.map((type, index) => (
                          <SelectItem key={index} label={type} value={type}>
                            {type}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                )}
              />
            </View>
            <View>
              <FormLabel>Have you had any criminal convictions in the last five years?</FormLabel>
              <FormField
                control={form.control}
                name="criminal_convictions"
                render={({ field }) => (

                  <Select onValueChange={(option) => field.onChange(option?.value || '')} value={{ value: field.value || "", label: field.value || "" }}>
                    <SelectTrigger className='w-[250px]'>
                      <SelectValue
                        className='text-foreground text-sm native:text-lg'
                        placeholder='Select an option'
                      />
                    </SelectTrigger>
                    <SelectContent insets={contentInsets} className='w-[250px] z-[1000] bg-white shadow-lg'>
                      <SelectGroup>
                        {YesNoTypeSchema.options.map((type, index) => (
                          <SelectItem key={index} label={type} value={type}>
                            {type}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                )}
              />
            </View>
          </View>
          <Button
            size="default"
            variant="crew"
            onPress={form.handleSubmit(onSubmit)}
            className="mt-4"
          >
            {form.formState.isSubmitting ? (
              <ActivityIndicator size="small" />
            ) : (
              <Text style={{ color: "#FFF000" }}>Save</Text>
            )}
          </Button>
        </Form>
      </CardContent>
    </Card >
  )

}